'use client';

import { linkToString } from '@/helper/urls';
import ChevronDownIcon from '@/public/icons/material/keyboard-arrow-down-rounded.svg';
import type { IHelpCenterCategories } from '@/storyblok/PageHelpCenterHome';
import { Select } from '@base-ui/react/select';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './sideNavigation.module.scss';

export const SideNavigation: React.FC<{
  categories: Array<IHelpCenterCategories>;
  selected?: string;
}> = (props) => {
  const { t } = useTranslation();
  const router = useRouter();

  const handleValueChange = useCallback(
    (slug: string | null) => {
      if (slug) {
        router.push(linkToString(slug));
      }
    },
    [router]
  );

  return (
    <nav data-testid="side-navigation">
      <div className={classes.sideNavigationMobile}>
        <Select.Root value="" onValueChange={handleValueChange}>
          <Select.Trigger
            aria-label={t('helpCenter.topicSelection')}
            className={classes.mobileTrigger}
            data-testid="side-navigation-topic-selection"
            id="side-navigation-topic-selection"
          >
            <span className={classes.mobileTriggerValue}>
              <Select.Value>{t('helpCenter.topicSelection')}</Select.Value>
            </span>
            <Select.Icon className={classes.mobileTriggerIcon}>
              <ChevronDownIcon />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Backdrop className={classes.mobileBackdrop} />
            <Select.Positioner
              align="start"
              alignItemWithTrigger={false}
              className={classes.mobilePositioner}
              side="bottom"
              sideOffset={4}
            >
              <Select.Popup className={classes.mobilePopup} data-swd-select-popup="">
                <Select.List>
                  {props.categories.map((category, index) => (
                    <Select.Item
                      key={index}
                      aria-selected={category.title === props.selected}
                      className={classes.mobileListItem}
                      data-testid={'side-navigation-topic-selection-item' + Number(index + 1)}
                      value={category.link}
                    >
                      <span className={classes.mobileListItemIcon} data-slot="side-nav-icon">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          className={classes.mobileListItemImg}
                          src={category.icon}
                          alt={category.alt}
                          width={24}
                          height={24}
                        />
                      </span>
                      <Select.ItemText>{category.title}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </div>

      <div className={classes.sideNavigationDesktop}>
        <ul data-testid="side-navigation-navigation-list" className="navigation-list">
          {props.categories.map((category, index) => (
            <li data-testid={'side-navigation-navigation-list-item' + Number(index + 1)} key={index}>
              <Link
                data-testid={'side-navigation-navigation-list-item' + Number(index + 1) + 'link'}
                prefetch={true}
                href={linkToString(category.link)}
                className={classNames({ [classes.selected!]: category.title === props.selected })}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={category.icon} alt={category.alt} width={24} height={24} />
                <span>{category.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};
export default SideNavigation;
